/* eslint-disable react-refresh/only-export-components -- `trackRouteChunk` is the signal RoutePendingBar reads and ships beside it; HMR falls back to reload here. */
import { useEffect, useSyncExternalStore } from 'react';
import { useLocation, useNavigate, useNavigation, useRouteError } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { ErrorScreen } from '../ErrorBoundary';

/**
 * What a route renders while it is arriving, and what it renders when it fails
 * (OFFICE-UI-REDESIGN-PLAN.md §1.5).
 *
 * The three states these replace were all one line each: `Loading…` at 32px of
 * padding as the entire viewport, nothing at all during a client-side
 * navigation, and an inline-styled `window.location.reload()` button for every
 * error in the application.
 */

/* ── Loading ─────────────────────────────────────────────────────────── */

/** Eight rows is a screenful at --row-compact without pretending to know the count. */
const SKELETON_ROWS = [0, 1, 2, 3, 4, 5, 6, 7];

/*
  Nine nav rows and three foot rows, because that is what `visibleOfficeNav`
  yields for the widest role — a skeleton that draws five and resolves into
  nine moves the foot 128px down on hydrate.

  The label widths are RAGGED on purpose. Nine identical bars read as a loading
  animation; nine bars of different lengths read as a list of words, which is
  what is actually arriving. The numbers are roughly the real labels at
  `text-body`: Home, Floor plan, Orders, Products, Customers, Coupons,
  Analytics, Inventory, Media.
*/
const RAIL_ROWS = [0, 1, 2, 3, 4, 5, 6, 7, 8];
const RAIL_LABEL_WIDTHS = [40, 68, 50, 62, 74, 60, 64, 66, 44];
const FOOT_ROWS = [0, 1, 2];
/** Four destinations and More — the phone bar's five cells. */
const TAB_CELLS = [0, 1, 2, 3, 4];

/**
 * The shape of an office page, not a word.
 *
 * A cold load of /products spends its first moment inside a Suspense fallback,
 * and a single left-aligned string there reads as a broken page rather than as
 * a page on its way. The geometry is OfficePage's (features/office/shared/
 * page-chrome.tsx): the full-bleed header band on --card over the px-4/md:px-6
 * body, so the real page lands where the skeleton was instead of jumping.
 *
 * Every number below is therefore a MIRROR, not a choice, and it has to be
 * re-checked whenever one of them moves. The density pass re-cut all four:
 * the header band is 64px (py-3.5 + a 36px title row) where it used to draw an
 * eyebrow and a note line for 104; the filter line moved off the page and into
 * the card as TableShell's 44px `toolbarLayout="fill"` band; the head is
 * --table-head-compact (36px) and the rows --row-compact (44px). A skeleton
 * still on the old register is worse than no skeleton — it promises a layout
 * and then moves it out from under the reader on hydrate.
 *
 * One announcement, not seven: `role="status"` + `aria-busy` on the wrapper
 * with a single sr-only word, and the bars themselves are empty divs a screen
 * reader has nothing to say about.
 */
export function RouteSkeleton() {
  return (
    <div
      data-slot="route-skeleton"
      role="status"
      aria-busy="true"
      className="flex min-h-full w-full min-w-0 flex-col"
    >
      <span className="sr-only">Loading</span>
      {/*
        MIRROR CORRECTION, and it was 12px out. `min-h-control-md` is a
        border-box height, so on the BAND it was satisfied by the padding alone:
        24px of bar + 28px of py-3.5 + 1px hairline = 53px, against OfficePage's
        real 14 + 36 + 14 + 1 = 65. The 36px floor belongs to the title ROW, one
        level in, exactly as it does in page-chrome — otherwise the page jumped
        12px up the moment the chunk landed, which is precisely the jump this
        skeleton exists to prevent.
      */}
      <div className="flex shrink-0 flex-col border-b border-border bg-card px-4 py-3.5 md:px-6">
        <div className="flex min-h-control-md items-center">
          <Skeleton className="h-6 w-40" />
        </div>
      </div>
      <div className="flex flex-col gap-4 px-4 pt-4 pb-12 md:px-6 md:pt-6">
        {/*
          No `shadow-card`. This mirrors OfficeCard, and OfficeCard has been
          border-only since the elevation pass (card.tsx:126) — the skeleton was
          the last thing still drawing the lift, so the page settled by half a
          pixel of shadow on hydrate. It matters more now that the card sits on
          the panel's white rather than on the grey: a halo the ground used to
          absorb is visible against --card.
        */}
        <div className="overflow-hidden rounded-tile border border-border bg-card">
          {/* TableShell's `fill` toolbar band: 44px, px-cell, no vertical padding. */}
          <div className="flex min-h-row-compact items-center gap-2 border-b border-border px-cell">
            <Skeleton className="h-3.5 w-full max-w-48" />
            <Skeleton className="ml-auto h-control-sm w-24" />
          </div>
          <div className="h-table-head-compact border-b border-border bg-table-head-bg" />
          {SKELETON_ROWS.map((row) => (
            <div
              key={row}
              className="flex h-row-compact items-center gap-4 border-b border-column-divider px-cell last:border-b-0"
            >
              <Skeleton className="size-8 shrink-0 rounded-ctl" />
              <Skeleton className="h-3 w-48" />
              <Skeleton className="hidden h-3 w-24 sm:block" />
              <Skeleton className="ml-auto h-3 w-16" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * The shape of the whole OFFICE, for the moment before the office exists.
 *
 * `RouteSkeleton` above is a PAGE skeleton: a header band and a table card, no
 * rail, no panel — correct, because it renders inside the content panel that
 * the mounted shell is already drawing around it. It was also wired as the
 * fallback for the boundary around `<OfficeShell />` itself, and there it is
 * the wrong drawing entirely. On a cold load of /customers the shell's own
 * chunk is still in flight, so what the operator got was a full-window table
 * card with no sidebar at all, then the sidebar and the panel snapping in
 * underneath it a moment later (owner's screenshot, 2026-08-20). A skeleton
 * that resolves into a different layout is worse than no skeleton: it promises
 * a page and then moves it.
 *
 * So the two boundaries get two drawings. This one mirrors the SHELL —
 * `bg-rail` at 200px with the head, the nav and the foot roughed in, then the
 * 8px frame and the white panel — and puts the page skeleton inside the panel
 * where the page will land. Every number here is a MIRROR of OfficeShell.tsx
 * and has to be re-checked when one of them moves: 200px rail at `px-2`, the
 * head at `pt-3.5` so the mark lands on the page title's centre, 32px rows
 * (`h-8`) with the label column starting 48px in.
 *
 * The rail is `hidden md:flex` exactly as the real one is, so below md this
 * degrades to the page skeleton — plus the phone bar, which the real shell
 * draws there and which reserves its own 76px for the same anti-shift reason
 * every other number in this drawing exists for.
 */
export function ShellSkeleton() {
  return (
    /*
      NO `role="status"` HERE. `RouteSkeleton` inside the panel already is one,
      and nesting two live regions announces "Loading" twice for one wait. The
      rail beside it is `aria-hidden` — it is a drawing of a navigation, not a
      navigation — so the whole of this element's accessible content is the one
      status the page skeleton carries.
    */
    <div
      data-slot="shell-skeleton"
      className="flex h-dvh w-full flex-col overflow-hidden bg-rail"
    >
      <div className="flex min-h-0 flex-1">
        <aside
          aria-hidden
          className="hidden h-full w-[200px] shrink-0 flex-col bg-rail px-2 md:flex"
        >
          {/* The head: the mark and the organization on one line, at pt-3.5. */}
          <div className="flex h-8 shrink-0 items-center gap-2.5 px-2.5 pt-3.5">
            <Skeleton className="size-5 shrink-0 rounded-ctl" />
            <Skeleton className="h-3 w-24" />
          </div>

          <div className="flex flex-1 flex-col gap-0.5 pt-6">
            {RAIL_ROWS.map((row) => (
              <div key={row} className="flex h-8 items-center gap-2.5 px-2.5">
                <Skeleton className="size-5 shrink-0 rounded-ctl" />
                <Skeleton className="h-3 flex-1" style={{ maxWidth: RAIL_LABEL_WIDTHS[row] }} />
              </div>
            ))}
          </div>

          {/* The foot: search, Settings, account — three rows, same geometry. */}
          <div className="flex shrink-0 flex-col gap-0.5 pb-3">
            {FOOT_ROWS.map((row) => (
              <div key={row} className="flex h-8 items-center gap-2.5 px-2.5">
                <Skeleton className="size-5 shrink-0 rounded-ctl" />
                <Skeleton className="h-3 w-16" />
              </div>
            ))}
          </div>
        </aside>

        <main className="relative flex min-w-0 flex-1 flex-col overflow-hidden bg-rail md:p-2">
          <div className="min-h-0 flex-1 overflow-hidden bg-card md:rounded-card md:shadow-panel dark:md:border dark:md:border-border">
            <RouteSkeleton />
          </div>
        </main>
      </div>

      {/*
        THE BAR IS DRAWN HERE FOR THE REASON THE RAIL IS: so the shell does not
        change SHAPE when the real one arrives. Without it a cold phone load
        paints a full-height panel, then loses 76px off the bottom of it a
        moment later and shifts every row the reader had already started
        reading. Same geometry as BottomTabs — the min-height, the inset, the
        hairline, five cells — and `aria-hidden`, because a drawing of a
        navigation is not a navigation.
      */}
      <div
        aria-hidden
        className="grid min-h-[calc(76px+env(safe-area-inset-bottom))] shrink-0 auto-cols-fr grid-flow-col border-t border-border bg-rail pb-[env(safe-area-inset-bottom)] md:hidden"
      >
        {TAB_CELLS.map((cell) => (
          <div key={cell} className="flex flex-col items-center justify-center gap-1.5">
            <Skeleton className="size-5 rounded-ctl" />
            <Skeleton className="h-2.5 w-10" />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Pending navigation ──────────────────────────────────────────────── */

/*
  Why this needs a store of its own, and cannot just read `useNavigation()`.

  `useNavigation()` leaves 'idle' only while the data router is resolving a
  route — loaders, actions, route-level `lazy`. This route table has none of
  those: every screen is a React.lazy() element, so the router finishes the
  navigation on the spot and the wait the user actually experiences is the chunk
  download that happens afterwards, during render.

  React makes that wait invisible on purpose. react-router v7 commits navigation
  state inside `startTransition`, and React will not commit a transition whose
  render suspended — so while the chunk is in flight NOTHING already on screen
  re-renders, and no hook reading router state can light a progress bar. The
  previous page simply sits there.

  A sync-lane update CAN commit while a transition is suspended; that is what
  lanes are for. So the signal comes from outside the transition entirely: from
  the `lazy()` factory itself, which every route in router.tsx runs through
  `trackRouteChunk`. The factory fires the moment React first renders that route
  and its promise settles when the chunk lands, which is exactly the window to
  report. Listeners are notified in a microtask because the factory is called
  DURING render, and a listener that called setState synchronously there would
  be updating one component while another renders.

  `useNavigation()` is still read below: the day a route grows a loader, that is
  the state that will be pending, and this bar should already cover it.
*/

/**
 * Under this, a navigation reads as instant and a progress bar is a flicker
 * that costs more attention than the wait it reports. Above it, silence reads
 * as a dead click.
 */
const SHOW_AFTER_MS = 150;

/*
  The delay lives in the store, not in the component.

  A `useState` + `useEffect` version of "wait 150ms, then show" has to clear its
  own flag on the way back down, which is a setState in an effect body — the
  cascading-render pattern react-hooks/set-state-in-effect exists to stop. Held
  here, the whole thing is one timer for the whole app and the component becomes
  a pure read.
*/
let inFlightChunks = 0;
let routerPending = false;
let barVisible = false;
let showTimer: number | undefined;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

function subscribeBar(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function isBarVisible(): boolean {
  return barVisible;
}

/** Called from a microtask or a timer — never during a render. */
function syncBar(): void {
  const pending = inFlightChunks > 0 || routerPending;
  if (pending) {
    if (barVisible || showTimer !== undefined) return;
    showTimer = window.setTimeout(() => {
      showTimer = undefined;
      barVisible = true;
      emit();
    }, SHOW_AFTER_MS);
    return;
  }
  if (showTimer !== undefined) {
    window.clearTimeout(showTimer);
    showTimer = undefined;
  }
  if (barVisible) {
    barVisible = false;
    emit();
  }
}

/**
 * Counts a route chunk while it downloads. Called inside the `lazy()` factory
 * (router.tsx) around the `import()` itself, so the `import()` specifier stays
 * a literal and Vite splits exactly the same chunks it did before.
 */
export function trackRouteChunk<T>(pending: Promise<T>): Promise<T> {
  inFlightChunks += 1;
  queueMicrotask(syncBar);
  return pending.finally(() => {
    inFlightChunks -= 1;
    queueMicrotask(syncBar);
  });
}

function setRouterPending(pending: boolean): void {
  if (routerPending === pending) return;
  routerPending = pending;
  syncBar();
}

/**
 * A 2px indeterminate bar across the top of the content area, for navigations
 * slow enough to notice.
 *
 * It pulses rather than travels because a travelling bar needs its own
 * keyframes and `--animate-*` lives in tokens.css. The pulse is the shared
 * `animate-pulse` the skeletons use, so prefers-reduced-motion collapses it
 * through the global rule (tokens.css) instead of through a second opinion
 * here: motion off leaves a static bar, which still answers "is it working?".
 *
 * Mounted today from `RouteContent` (router.tsx), which is as far inside the
 * shell as the route table reaches: that puts it inside `<main
 * data-slot="office-content">`, where `sticky` keeps it pinned to the top of a
 * scrolled page. Its real home is the shell itself — rendering it as the last
 * child of `<header data-slot="office-topbar">` would sit it on the chrome
 * seam, above the scroller rather than inside it. That move is one line in each
 * shell plus deleting the line in RouteContent, and it belongs to whoever owns
 * OfficeShell.tsx / PosShell.tsx.
 */
export function RoutePendingBar() {
  const navigation = useNavigation();
  const routerBusy = navigation.state !== 'idle';
  const visible = useSyncExternalStore(subscribeBar, isBarVisible);

  // Reporting React state TO an external system is what an effect is for. The
  // chunk counter pushes itself; a loader-driven navigation has to be pushed.
  useEffect(() => {
    setRouterPending(routerBusy);
    return () => setRouterPending(false);
  }, [routerBusy]);

  if (!visible) return null;

  return (
    // Zero-height and sticky: it pins to the top of the scroller it lives in
    // without taking a row from the page, and without a positioned wrapper the
    // content would have to leave room for.
    <div className="pointer-events-none sticky top-0 z-bulkbar h-0">
      <div
        data-slot="route-pending-bar"
        role="progressbar"
        aria-label="Loading the next page"
        className="absolute inset-x-0 top-0 h-0.5 overflow-hidden bg-border-2"
      >
        <div className="h-full w-full animate-pulse bg-primary" />
      </div>
    </div>
  );
}

/* ── Failure ─────────────────────────────────────────────────────────── */

/**
 * The route group's `errorElement` — the boundary that lets the chrome survive.
 *
 * React Router renders an errorElement in place of the element belonging to the
 * nearest matched route that declares one. Hanging it off the pathless content
 * route (router.tsx) therefore replaces the content area and nothing else: the
 * sidebar, the topbar, the org and branch switchers all stay mounted, and the
 * user can leave the broken page by clicking the nav rather than by reloading
 * the application.
 *
 * "Try again" re-navigates to the current URL instead of reloading, because
 * that is precisely the reset react-router already implements: its internal
 * render-error boundary drops the error it caught as soon as `location` changes
 * identity, which a replace to the same path does. It is also the same reset
 * the user gets for free by clicking any other nav entry.
 *
 * No logging here — react-router has already reported the error to the console
 * by the time this renders.
 */
export function RouteErrorFallback() {
  const error = useRouteError();
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  const asError = error instanceof Error ? error : null;

  return (
    <ErrorScreen
      scope="page"
      title="This page stopped working"
      error={asError}
      detail={asError?.stack ?? null}
      onRetry={() => navigate(`${pathname}${search}${hash}`, { replace: true })}
    >
      {/* Deliberately does not name the back office: this same errorElement is
          mounted on the POS group too (router.tsx:302), and telling a cashier
          whose register just failed that "the rest of the back office" is fine
          describes a place they are not in. */}
      Everything else is still running — pick another screen from the navigation, or try this one
      again.
    </ErrorScreen>
  );
}
